import React from 'react'
import moment from 'moment'
import capitalize from 'lodash/capitalize'
import { Task } from '@core/entities/task/Task'
import { Box, Flex, Text } from '@fundamentals'
import { Card } from '@common/material'
import { TaskTemplateProperties } from '@core/entities/task/TaskTemplateProperties'
import { localizedStrings } from '@core/strings'
import { Section } from '@common/Section'
import { WarningAmber } from '@mui/icons-material'
import { AttachmentInput } from '@elements/Questions/AttachmentInput'

type TaskPropertiesCardProps = {
  task: Task
}

export const TaskPropertiesCard: React.FC<TaskPropertiesCardProps> = ({
  task,
}) => {
  if (!task) {
    return null
  }

  return (
    <Card sx={{ p: 1.5 }}>
      <Section
        labelProps={{ flex: 1 }}
        label={localizedStrings.assignTo}
        horizontal
      >
        <Flex flex={5} justifyContent='flex-start'>
          <Text data-test='assignee-name' showTooltip variant='body2'>
            {task.assignee.getFullName()}
          </Text>
        </Flex>
      </Section>

      <Section
        labelProps={{ flex: 1 }}
        label={localizedStrings.priority}
        mt={1}
        horizontal
      >
        <Flex alignItems='center' sx={{ flex: 5 }}>
          <Box
            height={16}
            width={16}
            borderRadius='50%'
            sx={{
              backgroundColor: TaskTemplateProperties.getColorForPriority(
                task.priority,
              ),
              mr: 1,
            }}
          />
          <Text variant='body2'>{capitalize(task?.priority)}</Text>
        </Flex>
      </Section>

      <Section
        labelProps={{ flex: 1 }}
        label={localizedStrings.location}
        mt={1}
        horizontal
      >
        <Flex flex={5} justifyContent='flex-start'>
          <Text showTooltip variant='body2'>
            {task.location || '-'}
          </Text>
        </Flex>
      </Section>

      <Section
        labelProps={{ flex: 1 }}
        label={localizedStrings.dueDate}
        mt={1}
        horizontal
      >
        <Flex flex={5} justifyContent='flex-start'>
          <Text
            showTooltip
            sx={{
              color: task.isOverdue() ? 'error.main' : 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
            variant='body2'
          >
            {task.dueDate
              ? moment(task.dueDate).format('Do MMM YYYY HH:mm')
              : '-'}
            {task.isOverdue() && (
              <Box
                component='span'
                sx={{
                  color: 'error.main',
                  display: 'inline-flex',
                }}
              >
                <WarningAmber />
              </Box>
            )}
          </Text>
        </Flex>
      </Section>

      <Section
        labelProps={{ flex: 1 }}
        label={localizedStrings.created}
        mt={1}
        horizontal
      >
        <Flex flex={5} justifyContent='flex-start'>
          <Text showTooltip variant='body2'>
            {moment(task.createdOn).format('Do MMM YYYY HH:mm')}
          </Text>
        </Flex>
      </Section>

      <Section
        labelProps={{ flex: 1 }}
        label={localizedStrings.description}
        mt={1}
        horizontal
      >
        <Flex flex={5} justifyContent='flex-start'>
          <Text variant='body2'>{task.summary}</Text>
        </Flex>
      </Section>

      {task.attachments?.data.length > 0 && (
        <Section
          labelProps={{ flex: 1 }}
          label={localizedStrings.attachments}
          mt={1}
          horizontal
        >
          <Flex flexWrap='wrap' my={1} sx={{ flex: 5 }}>
            <AttachmentInput
              size='small'
              inputProps={{
                value: task.attachments.data.map((attachment) => {
                  if (attachment?.mimeType?.includes('video')) {
                    return { ...attachment, type: 'video/mp4' }
                  }

                  if (
                    attachment.type?.includes('application/pdf') ||
                    attachment.type?.includes('PDF')
                  ) {
                    return { ...attachment, type: 'application/pdf' }
                  }

                  return { ...attachment, type: 'image' }
                }),
              }}
              readOnly
              borderLess
              thumbnailFitToContent
              preventDialogClose={false}
            />
          </Flex>
        </Section>
      )}
    </Card>
  )
}
